import React, { useState, useEffect, useMemo } from "react";
import API from "../../utils/api";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";

// ── CSS ───────────────────────────────────────────────────────────────────────
const CSS = `
  .ex-grid-4    { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
  .ex-grid-2    { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .ex-grid-3    { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
  .ex-bar-wrap  { display: flex; align-items: center; gap: 10px; }
  @media (max-width: 900px) {
    .ex-grid-4 { grid-template-columns: 1fr 1fr !important; }
    .ex-grid-3 { grid-template-columns: 1fr !important; }
  }
  @media (max-width: 600px) {
    .ex-grid-4 { grid-template-columns: 1fr !important; }
    .ex-grid-2 { grid-template-columns: 1fr !important; }
  }
`;

// ── Constants ─────────────────────────────────────────────────────────────────
const COMPANY_ACCENT = { ebek: "#E31E24", ebekmed: "#2AB5A5" };
const COMPANY_BG     = { ebek: "rgba(227,30,36,0.12)", ebekmed: "rgba(42,181,165,0.12)" };
const COMPANY_LABEL  = { ebek: "Ebek", ebekmed: "Ebek Med" };
const MONTHS_SHORT   = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const CATEGORY_COLORS = {
  Travel:"#4f46e5", Hotel:"#0891b2", Food:"#059669", Fuel:"#d97706",
  Courier:"#7C3AED", Stationeries:"#E31E24", Prints:"#ec4899", Miscellaneous:"#6b7280",
};
const CATEGORY_ICONS = {
  Travel:"🚂", Hotel:"🏨", Food:"🍽️", Fuel:"⛽",
  Courier:"📦", Stationeries:"📝", Prints:"🖨️", Miscellaneous:"📌",
};
const TRIP_CATEGORIES = ["OET Examination","Cambridge Examination","Other Examination","Marketing","Others"];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 4 }, (_, i) => currentYear - i);

// ── Formatters ────────────────────────────────────────────────────────────────
function fmtShort(n) {
  const v = parseFloat(n || 0);
  if (v >= 10000000) return `₹${(v/10000000).toFixed(2)}Cr`;
  if (v >= 100000)   return `₹${(v/100000).toFixed(2)}L`;
  if (v >= 1000)     return `₹${(v/1000).toFixed(1)}K`;
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
}
function fmtFull(n) { return `₹${parseFloat(n||0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}` }
function getRange(year, month) {
  if (month) {
    const m  = month.padStart(2,"0");
    const dy = new Date(parseInt(year), parseInt(month), 0).getDate();
    return { from: `${year}-${m}-01`, to: `${year}-${m}-${String(dy).padStart(2,"0")}` };
  }
  const today = new Date().toISOString().substring(0,10);
  return { from: `${year}-01-01`, to: year === String(currentYear) ? today : `${year}-12-31` };
}

// ── SVG: Donut ────────────────────────────────────────────────────────────────
function polar(cx,cy,r,deg) {
  const rad = ((deg-90)*Math.PI)/180;
  return { x: cx+r*Math.cos(rad), y: cy+r*Math.sin(rad) };
}
function arcPath(cx,cy,oR,iR,s,e) {
  const delta = Math.min(e-s,359.9999), end = s+delta, large = delta>180?1:0;
  const o1=polar(cx,cy,oR,s),o2=polar(cx,cy,oR,end),i1=polar(cx,cy,iR,end),i2=polar(cx,cy,iR,s);
  return `M${o1.x},${o1.y} A${oR},${oR},0,${large},1,${o2.x},${o2.y} L${i1.x},${i1.y} A${iR},${iR},0,${large},0,${i2.x},${i2.y} Z`;
}
function Donut({ segments, size=160, thickness=32, label="" }) {
  const [hov,setHov] = useState(null);
  const total = segments.reduce((s,d)=>s+d.value,0);
  if (!total) return <div style={{height:size,display:"flex",alignItems:"center",justifyContent:"center",color:"#9ca3af",fontSize:12}}>No data</div>;
  const cx=size/2,cy=size/2,oR=size/2-6,iR=oR-thickness;
  let cum=0;
  const segs = segments.map(d=>{
    const start=cum*360; cum+=d.value/total; const end=cum*360;
    return{...d,start,end,pct:(d.value/total*100).toFixed(1)};
  });
  const h = hov!=null?segs[hov]:null;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{overflow:"visible",flexShrink:0}}>
      {segs.map((s,i)=>(
        <path key={i} d={arcPath(cx,cy,hov===i?oR+6:oR,iR,s.start,s.end)}
          fill={s.color} opacity={hov!=null&&hov!==i?0.35:1}
          style={{cursor:"pointer",transition:"all 0.15s"}}
          onMouseEnter={()=>setHov(i)} onMouseLeave={()=>setHov(null)}/>
      ))}
      <text x={cx} y={cy-8}  textAnchor="middle" fontSize={11} fontWeight="800" fill="#f9fafb">{h?fmtShort(h.value):fmtShort(total)}</text>
      <text x={cx} y={cy+8} textAnchor="middle" fontSize={9} fill={h?h.color:"#9ca3af"} fontWeight="700">{h?`${h.pct}%`:label}</text>
    </svg>
  );
}

// ── SVG: Bar Trend Chart ──────────────────────────────────────────────────────
function BarTrend({ data, colorA="#E31E24", colorB="#2AB5A5", showBoth=true }) {
  const W=420, H=160, padL=44, padB=28, padT=12, padR=8;
  const plotW=W-padL-padR, plotH=H-padT-padB;
  const maxVal = Math.max(...data.map(d=>(showBoth?Math.max(d.a,d.b||0):d.a)),1);
  const barW   = Math.floor(plotW/data.length*0.35);
  const gap    = Math.floor(plotW/data.length*0.1);
  const step   = plotW/data.length;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{overflow:"visible"}}>
      {/* Y axis labels */}
      {[0,0.25,0.5,0.75,1].map(f=>{
        const y = padT+plotH*(1-f);
        return (
          <g key={f}>
            <line x1={padL} x2={padL+plotW} y1={y} y2={y} stroke="rgba(255,255,255,0.08)" strokeWidth={1}/>
            <text x={padL-4} y={y+4} textAnchor="end" fontSize={8} fill="#e2e8f0">{fmtShort(maxVal*f)}</text>
          </g>
        );
      })}
      {data.map((d,i)=>{
        const cx = padL + i*step + step/2;
        const hA = (d.a/maxVal)*plotH;
        const hB = showBoth&&d.b!=null ? (d.b/maxVal)*plotH : 0;
        const xA = cx-(showBoth?(barW+gap/2):barW/2);
        const xB = cx+gap/2;
        return (
          <g key={i}>
            <rect x={xA} y={padT+plotH-hA} width={barW} height={hA} fill={colorA} rx={2} opacity={0.9}/>
            {showBoth&&hB>0&&<rect x={xB} y={padT+plotH-hB} width={barW} height={hB} fill={colorB} rx={2} opacity={0.9}/>}
            <text x={cx} y={H-4} textAnchor="middle" fontSize={8} fill="#d1d5db">{d.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, sub, color="#7C3AED" }) {
  return (
    <div className="card" style={{padding:"16px 18px",borderTop:`3px solid ${color}`}}>
      <div style={{fontSize:22,marginBottom:4}}>{icon}</div>
      <div style={{fontSize:11,color:"#9ca3af",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>{label}</div>
      <div style={{fontSize:22,fontWeight:900,color:color}}>{value}</div>
      {sub&&<div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>{sub}</div>}
    </div>
  );
}

// ── Company Column ────────────────────────────────────────────────────────────
function CompanyBlock({ company, rows }) {
  const accent = COMPANY_ACCENT[company];
  const bg     = COMPANY_BG[company];
  const label  = COMPANY_LABEL[company];

  const totalSpend    = rows.reduce((s,r)=>s+parseFloat(r.amount||0),0);
  const totalTrips    = new Set(rows.map(r=>r.trip_id)).size;
  const totalEmployees= new Set(rows.map(r=>r.employee_email)).size;
  const totalAdvance  = rows.reduce((s,r)=>s+parseFloat(r.advance_amount||0),0);

  const byCategory = {};
  rows.forEach(r=>{
    byCategory[r.category]=(byCategory[r.category]||0)+parseFloat(r.amount||0);
  });
  const catSegments = Object.entries(byCategory)
    .sort((a,b)=>b[1]-a[1])
    .map(([k,v])=>({label:k,value:v,color:CATEGORY_COLORS[k]||"#6b7280"}));

  const byEmp = {};
  rows.forEach(r=>{
    if(!byEmp[r.employee_name]) byEmp[r.employee_name]={name:r.employee_name,dept:r.department,total:0};
    byEmp[r.employee_name].total+=parseFloat(r.amount||0);
  });
  const topEmps = Object.values(byEmp).sort((a,b)=>b.total-a.total).slice(0,5);
  const maxEmp  = topEmps[0]?.total||1;

  return (
    <div className="card" style={{padding:0,borderTop:`4px solid ${accent}`,overflow:"hidden"}}>
      {/* Header */}
      <div style={{padding:"14px 20px",background:bg,borderBottom:`1px solid ${accent}44`}}>
        <div style={{fontSize:16,fontWeight:900,color:accent}}>{label}</div>
        <div style={{fontSize:11,color:"#9ca3af",marginTop:2}}>Company Performance</div>
      </div>

      {/* KPIs */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:0,borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
        {[
          {l:"Total Spend",v:fmtShort(totalSpend)},
          {l:"Total Advance",v:fmtShort(totalAdvance)},
          {l:"Trips",v:totalTrips},
          {l:"Employees",v:totalEmployees},
        ].map((k,i)=>(
          <div key={i} style={{padding:"12px 16px",borderRight:i%2===0?"1px solid rgba(255,255,255,0.08)":"none",borderBottom:i<2?"1px solid rgba(255,255,255,0.08)":"none"}}>
            <div style={{fontSize:10,color:"#9ca3af",fontWeight:700,textTransform:"uppercase"}}>{k.l}</div>
            <div style={{fontSize:18,fontWeight:800,color:accent,marginTop:2}}>{k.v}</div>
          </div>
        ))}
      </div>

      {/* Category Donut */}
      {catSegments.length>0&&(
        <div style={{padding:"16px 20px",borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
          <div style={{fontSize:12,fontWeight:700,color:"#d1d5db",marginBottom:10}}>By Category</div>
          <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
            <Donut segments={catSegments} size={120} thickness={26} label="Total"/>
            <div style={{flex:1,minWidth:100}}>
              {catSegments.slice(0,5).map((s,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:6,marginBottom:5}}>
                  <div style={{width:7,height:7,borderRadius:1,background:s.color,flexShrink:0}}/>
                  <span style={{fontSize:11,color:"#d1d5db",flex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                    {CATEGORY_ICONS[s.label]||"📌"} {s.label}
                  </span>
                  <span style={{fontSize:11,fontWeight:700,color:accent,flexShrink:0}}>{fmtShort(s.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Top Employees */}
      {topEmps.length>0&&(
        <div style={{padding:"16px 20px"}}>
          <div style={{fontSize:12,fontWeight:700,color:"#d1d5db",marginBottom:10}}>Top Spenders</div>
          {topEmps.map((e,i)=>(
            <div key={i} style={{marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                <span style={{fontSize:12,color:"#f9fafb",fontWeight:i===0?700:500}}>
                  {i===0?"🥇":i===1?"🥈":i===2?"🥉":"  "} {e.name}
                </span>
                <span style={{fontSize:12,fontWeight:700,color:accent}}>{fmtShort(e.total)}</span>
              </div>
              <div style={{height:5,background:"rgba(255,255,255,0.1)",borderRadius:3}}>
                <div style={{height:5,background:accent,borderRadius:3,width:`${(e.total/maxEmp*100).toFixed(1)}%`,transition:"width 0.4s"}}/>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function ExecutiveDashboard() {
  const [year,  setYear]  = useState(String(currentYear));
  const [month, setMonth] = useState("");
  const [company, setCompany] = useState("all"); // filter for analytics/reports drill-down
  const [tripCat, setTripCat] = useState("");
  const [rows,  setRows]  = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    const { from, to } = getRange(year, month);
    setLoading(true);
    try {
      const r = await API.get(`/reports/expenses?from=${from}&to=${to}&company=all`);
      setRows(r.data);
    } catch(err) {
      toast.error("Failed to load data");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [year, month]);

  // ── Derived data ─────────────────────────────────────────────────────────
  const filteredRows = useMemo(()=>{
    if(!tripCat) return rows;
    return rows.filter(r=>String(r.trip_title||"").toLowerCase().includes(tripCat.toLowerCase()));
  },[rows,tripCat]);

  const ebekRows    = useMemo(()=>filteredRows.filter(r=>r.employee_company==="ebek"),    [filteredRows]);
  const ebekmedRows = useMemo(()=>filteredRows.filter(r=>r.employee_company==="ebekmed"), [filteredRows]);

  const totalSpend    = filteredRows.reduce((s,r)=>s+parseFloat(r.amount||0),0);
  const totalTrips    = useMemo(()=>new Set(filteredRows.map(r=>r.trip_id)).size, [filteredRows]);
  const totalEmployees= useMemo(()=>new Set(filteredRows.map(r=>r.employee_email)).size, [filteredRows]);
  const totalAdvance  = filteredRows.reduce((s,r)=>s+parseFloat(r.advance_amount||0),0);
  const balance       = totalAdvance - totalSpend;

  // Monthly trend
  const monthlyTrend = useMemo(()=>{
    const map = {};
    filteredRows.forEach(r=>{
      const d = new Date(r.expense_date);
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
      if(!map[key]) map[key]={a:0,b:0};
      if(r.employee_company==="ebek")    map[key].a+=parseFloat(r.amount||0);
      if(r.employee_company==="ebekmed") map[key].b+=parseFloat(r.amount||0);
    });
    return Object.entries(map).sort(([a],[b])=>a.localeCompare(b)).map(([k,v])=>{
      const [yr,mo] = k.split("-");
      return{ label:`${MONTHS_SHORT[parseInt(mo)-1]}'${yr.slice(2)}`, a:v.a, b:v.b };
    });
  },[filteredRows]);

  // Category breakdown (combined)
  const catBreakdown = useMemo(()=>{
    const map = {};
    filteredRows.forEach(r=>{ map[r.category]=(map[r.category]||0)+parseFloat(r.amount||0); });
    return Object.entries(map).sort(([,a],[,b])=>b-a)
      .map(([k,v])=>({label:k,value:v,color:CATEGORY_COLORS[k]||"#6b7280"}));
  },[filteredRows]);

  // Employee leaderboard
  const leaderboard = useMemo(()=>{
    const map = {};
    filteredRows.forEach(r=>{
      const k = r.employee_email;
      if(!map[k]) map[k]={name:r.employee_name,company:r.employee_company,dept:r.department,total:0,trips:new Set()};
      map[k].total += parseFloat(r.amount||0);
      map[k].trips.add(r.trip_id);
    });
    return Object.values(map).sort((a,b)=>b.total-a.total).slice(0,10).map(e=>({...e,tripCount:e.trips.size}));
  },[filteredRows]);

  const maxLead = leaderboard[0]?.total||1;

  const periodLabel = month ? `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][parseInt(month)-1]} ${year}` : `${year} (YTD)`;

  return (
    <div>
      <style>{CSS}</style>

      {/* Header */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <h1 className="page-title">👑 Executive Overview</h1>
          <p className="page-subtitle">Company-wide expense intelligence — {periodLabel}</p>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <select className="form-control" style={{fontSize:13,padding:"7px 12px",minWidth:90}}
            value={year} onChange={e=>{setYear(e.target.value);setMonth("");}}>
            {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
          <select className="form-control" style={{fontSize:13,padding:"7px 12px",minWidth:100}}
            value={month} onChange={e=>setMonth(e.target.value)}>
            <option value="">Full Year</option>
            {["01","02","03","04","05","06","07","08","09","10","11","12"].map((m,i)=>(
              <option key={m} value={m}>{["January","February","March","April","May","June","July","August","September","October","November","December"][i]}</option>
            ))}
          </select>
          <select className="form-control" style={{fontSize:13,padding:"7px 12px",minWidth:140}}
            value={tripCat} onChange={e=>setTripCat(e.target.value)}>
            <option value="">All Categories</option>
            {TRIP_CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
          <button className="btn btn-primary" style={{fontSize:13,padding:"7px 14px"}} onClick={fetchData} disabled={loading}>
            {loading?"Loading...":"🔄 Refresh"}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{textAlign:"center",padding:80,color:"#9ca3af"}}>Loading data...</div>
      ) : filteredRows.length===0 ? (
        <div className="card"><div className="empty-state"><div style={{fontSize:48}}>📊</div><h3>No data for this period</h3><p>Try selecting a different year or period</p></div></div>
      ) : (
        <>
          {/* ── Combined KPIs ── */}
          <div className="ex-grid-4" style={{marginBottom:20}}>
            <KpiCard icon="💸" label="Total Spend"    value={fmtShort(totalSpend)}    color="#7C3AED" sub={fmtFull(totalSpend)}/>
            <KpiCard icon="🏦" label="Total Advance"  value={fmtShort(totalAdvance)}  color="#d97706" sub={fmtFull(totalAdvance)}/>
            <KpiCard icon={balance>=0?"✅":"⚠️"} label="Net Balance" value={fmtShort(Math.abs(balance))} color={balance>=0?"#059669":"#DC2626"} sub={balance>=0?"Advance surplus":"Company owes employees"}/>
            <KpiCard icon="✈️" label="Total Trips"    value={totalTrips}              color="#0891b2" sub={`${totalEmployees} employees`}/>
          </div>

          {/* ── Company Comparison ── */}
          <div style={{marginBottom:8}}>
            <h2 style={{fontSize:15,fontWeight:800,color:"#f1f5f9",marginBottom:12}}>🏢 Company Breakdown</h2>
            <div className="ex-grid-2">
              <CompanyBlock company="ebek"    rows={ebekRows}/>
              <CompanyBlock company="ebekmed" rows={ebekmedRows}/>
            </div>
          </div>

          {/* ── Monthly Trend ── */}
          {monthlyTrend.length>1&&(
            <div className="card" style={{padding:"20px 24px",marginTop:20}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12,flexWrap:"wrap",gap:8}}>
                <div style={{fontSize:15,fontWeight:800,color:"#f1f5f9"}}>📅 Monthly Spend Trend</div>
                <div style={{display:"flex",gap:16}}>
                  <div style={{display:"flex",alignItems:"center",gap:5}}>
                    <div style={{width:12,height:12,borderRadius:2,background:COMPANY_ACCENT.ebek}}/>
                    <span style={{fontSize:12,color:"#d1d5db"}}>Ebek</span>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:5}}>
                    <div style={{width:12,height:12,borderRadius:2,background:COMPANY_ACCENT.ebekmed}}/>
                    <span style={{fontSize:12,color:"#d1d5db"}}>Ebek Med</span>
                  </div>
                </div>
              </div>
              <BarTrend data={monthlyTrend} colorA={COMPANY_ACCENT.ebek} colorB={COMPANY_ACCENT.ebekmed} showBoth={true}/>
            </div>
          )}

          {/* ── Category Breakdown + Leaderboard ── */}
          <div className="ex-grid-2" style={{marginTop:20,alignItems:"start"}}>

            {/* Category Donut */}
            <div className="card" style={{padding:"20px 24px"}}>
              <div style={{fontSize:15,fontWeight:800,color:"#f1f5f9",marginBottom:14}}>📊 Spend by Category</div>
              <div style={{display:"flex",alignItems:"center",gap:20,flexWrap:"wrap"}}>
                <Donut segments={catBreakdown} size={160} thickness={34} label="Total"/>
                <div style={{flex:1,minWidth:140}}>
                  {catBreakdown.map((s,i)=>(
                    <div key={i} style={{display:"flex",alignItems:"center",gap:7,marginBottom:7}}>
                      <div style={{width:9,height:9,borderRadius:2,background:s.color,flexShrink:0}}/>
                      <span style={{fontSize:12,color:"#d1d5db",flex:1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                        {CATEGORY_ICONS[s.label]||"📌"} {s.label}
                      </span>
                      <span style={{fontSize:12,fontWeight:700,color:s.color,flexShrink:0}}>{fmtShort(s.value)}</span>
                      <span style={{fontSize:10,color:"#9ca3af",flexShrink:0,width:34,textAlign:"right"}}>
                        {(s.value/totalSpend*100).toFixed(1)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Employee Leaderboard */}
            <div className="card" style={{padding:"20px 24px"}}>
              <div style={{fontSize:15,fontWeight:800,color:"#f1f5f9",marginBottom:14}}>🏆 Employee Leaderboard</div>
              {leaderboard.map((e,i)=>(
                <div key={i} style={{marginBottom:12}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                    <div style={{fontSize:13,fontWeight:i<3?700:500,color:"#f9fafb",display:"flex",alignItems:"center",gap:5}}>
                      <span style={{fontSize:15}}>{i===0?"🥇":i===1?"🥈":i===2?"🥉":`${i+1}.`}</span>
                      <span>{e.name}</span>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:13,fontWeight:700,color:COMPANY_ACCENT[e.company]}}>{fmtShort(e.total)}</div>
                      <div style={{fontSize:10,color:"#9ca3af"}}>{e.tripCount} trip{e.tripCount!==1?"s":""} · {COMPANY_LABEL[e.company]}</div>
                    </div>
                  </div>
                  <div style={{height:5,background:"rgba(255,255,255,0.1)",borderRadius:3}}>
                    <div style={{height:5,background:COMPANY_ACCENT[e.company]||"#7C3AED",borderRadius:3,width:`${(e.total/maxLead*100).toFixed(1)}%`,transition:"width 0.4s"}}/>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Quick Links ── */}
          <div className="card" style={{padding:"18px 24px",marginTop:20}}>
            <div style={{fontSize:14,fontWeight:800,color:"#f1f5f9",marginBottom:12}}>🔗 Drill Down</div>
            <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
              <Link to="/admin/analytics" className="btn btn-primary" style={{fontSize:13,padding:"8px 16px"}}>
                📈 Full Analytics
              </Link>
              <Link to="/admin/reports" className="btn btn-secondary" style={{fontSize:13,padding:"8px 16px"}}>
                📋 Download Reports
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
